import { baseScrollbarStyle } from "@shared/common/Themes/AppTheme";
import { SMALL_MOBILE } from "@shared/common/constants";
import { hexToRGB } from "@shared/utils/misc";
import { css } from "styled-components";
import { typography } from "@shared/common/Themes/typography";

const space = [4, 8, 16, 32, 64, 128];
const isDarkMode = true;
// Colors
const darkBgColor = "#151515";
const layoutBgColor = "#17191D";
const cardBgColor = "#24282D";
const secondaryCardBgColor = "#2B2F36";
const bgColor = layoutBgColor;
const mediumBgColor = "#30343B";
const lightBackground = hexToRGB(cardBgColor, 0.4);

const primaryBlue = "#70A8FF";
const midBlue = "#436599";
const lightBlue = hexToRGB(primaryBlue, 0.22);
const primaryGrey = "#FCFCFC";
const mediumGrey = "#B8BEC8";
const secondaryGrey = "#8992A3";
const lightestGrey = "#41464E";
const midDark = secondaryGrey;

const primaryText = primaryGrey;
const secondaryText = secondaryGrey;
const mediumText = mediumGrey;
const lightestText = lightestGrey;

const lightestBorderGrey = hexToRGB(secondaryGrey, 0.16);
const lightBorderGrey = hexToRGB(secondaryGrey, 0.21);
const mediumBorderGrey = hexToRGB(secondaryGrey, 0.27);
const hardBorderGrey = hexToRGB(secondaryGrey, 0.45);

const basic = "#FFFFFF";
const titleColor = "#FCFCFC";
const textColor = basic;
const buttonText = layoutBgColor;

const layoutFooterColor = "#1D2A52";
const titleCardBgColor = lightestGrey;
const pieChartBorderColor = lightestGrey;
const tableHoverBgColor = mediumBgColor;
const selectedTableColumnBgColor = mediumBgColor;
const emptyColor = mediumBgColor;
const hoverBgColor = cardBgColor;
const chartTicksColor = hexToRGB(secondaryGrey, 0.6);
const chartGridColor = hexToRGB(secondaryGrey, 0.2);
const avatarBgColor = "#41362D";
const avatarColor = "#FFA24C";
const activeMenuItemBgColor = hexToRGB(primaryBlue, 0.08);
const dashboardCoinsBgColor = mediumBgColor;
const dashboardStatisticsBgColor = cardBgColor;
const disabledInput = hexToRGB(cardBgColor, 0.7);
const tagBgColor = mediumBgColor;
const bannerTextColor = textColor;
const statisticsBgColor = darkBgColor;
const checkboxBorderColor = hardBorderGrey;
const filterChipsColor = mediumBgColor;
const activeBgColor = "#697387";
const filterChipsActiveColor = activeBgColor;
const tvCurrentPriceColor = activeBgColor;
const billingBgColor = layoutBgColor;
const paymentBgColor = "#202328";
const refreshSpinnerBgColor = lightestGrey;
const boxInCardColor = secondaryCardBgColor;
const highlightBgColor = lightBlue;
const cardLightBgColor = mediumBgColor;
const subMenuTitleBgColor = mediumBgColor;

const blues = [titleCardBgColor, "#CEDCFC", "#B3C9FB", "#77A2FF", primaryBlue];
const pieChartColors = ["#FFFFFF", "#70A8FF", "#C584FE", "#FFC554", "#78FFF7"];
const demoTagColors = ["#C584FE", "#70A8FF"];

export const colors = {
  green: "#4CD684",
  lightGreen: "#4CD684",
  red: "#FF6D6D",
  secondaryRed: "#C13434",
  orange: "#FFA24C",
  yellow: "#FFC554",
  darkYellow: "#D29317",
  gold: "#FFC554",
  lightGrey: "#E0E2E4",
  primary: primaryBlue,
  white: "#FFFFFF",
  disabledText: hexToRGB(secondaryText, 0.5),
  black: "#000000",
  purple: "#C584FE",
  pink: "#EF66FF",
  primaryGrey,
  mediumGrey,
  secondaryGrey,
  lightestBorderGrey,
  lightBorderGrey,
  primaryText,
  secondaryText,
  lightestText,
  primaryBlue,
  blues,
  pieChartColors,
  cardBgColor,
  bgColor,
  layoutBgColor,
  layoutFooterColor,
  titleColor,
  textColor,
  basic,
  buttonText,
  lightestGrey,
  titleCardBgColor,
  tableHoverBgColor,
  selectedTableColumnBgColor,
  emptyColor,
  hoverBgColor,
  lightBackground,
  chartTicksColor,
  chartGridColor,
  avatarBgColor,
  avatarColor,
  activeMenuItemBgColor,
  pieChartBorderColor,
  mediumBorderGrey,
  hardBorderGrey,
  dashboardCoinsBgColor,
  dashboardStatisticsBgColor,
  disabledInput,
  midDark,
  tagBgColor,
  tvCurrentPriceColor,
  boxInCardColor,
  lightBlue,
  highlightBgColor,
  subMenuTitleBgColor,
  checkboxBorderColor,
  filterChipsColor,
  bannerTextColor,
  midBlue,
  statisticsBgColor,
  demoTagColors,
  cardLightBgColor,
  refreshSpinnerBgColor,
  filterChipsActiveColor,
  mediumText,
  billingBgColor,
  paymentBgColor,
  mediumBgColor,
  activeBgColor,
  secondaryCardBgColor,
};

export const zIndexes = {
  modalBackground: 1099,
  modal: 1100,
  drawer: 1099,
};

export const scrollbarStyle = css`
  ${baseScrollbarStyle};
  ul::-webkit-scrollbar-thumb,
  div::-webkit-scrollbar-thumb,
  &::-webkit-scrollbar-thumb {
    border: 2px solid transparent;
    background-color: #8992a380;
    border-radius: 100px;
    background-clip: content-box;
    @media (max-width: ${SMALL_MOBILE}) {
      border: 1px solid transparent;
    }
  }
`;

export const shadows = {
  symbolCard: "rgb(96 118 145 / 23%) 2px 2px 20px 0px",
  card: "#0000003D 0px 6px 24px 0px",
  hoverCard: "rgba(0, 0, 0, 0.2) 0px 14px 24px",
};

const borders = {
  lightest: `1px solid ${colors.lightestBorderGrey}`,
  light: `1px solid ${colors.lightBorderGrey}`,
  medium: `1px solid ${colors.mediumBorderGrey}`,
  hard: `1px solid ${hexToRGB(secondaryGrey, 0.7)}`,
};

const DarkModeTheme = {
  isDarkMode,
  space,
  colors,
  zIndexes,
  typography,
  shadows,
  borders,
  scrollbarStyle: {},
};

DarkModeTheme.scrollbarStyle = scrollbarStyle;
export type DarkModeThemeType = typeof DarkModeTheme;

export default DarkModeTheme;
